import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { SkeletonTable } from '@/components/ui/skeleton-table';

/** POS-I371: the products of one category or brand, opened in place from the PRODUCTS count. */
export interface ProductsInTarget {
  kind: 'category' | 'brand';
  id: string;
  name: string;
}

interface ProductLite {
  id: string;
  name: string;
  sku?: string | null;
  salePrice?: number | string | null;
}

const LIMIT = 50;

export function ProductsInSheet({ target, onClose }: { target: ProductsInTarget | null; onClose: () => void }) {
  const { t } = useTranslation('products');
  const { data, isLoading, isError } = useQuery<{ data?: ProductLite[]; meta?: { total?: number } }>({
    queryKey: ['products-in', target?.kind, target?.id],
    enabled: !!target,
    queryFn: async () => {
      const params = new URLSearchParams({ page: '1', limit: String(LIMIT) });
      params.set(target!.kind === 'category' ? 'categoryId' : 'brandId', target!.id);
      const res = await api.get(`/products?${params}`);
      return res.data;
    },
  });
  const rows = data?.data ?? [];
  const total = data?.meta?.total ?? rows.length;

  return (
    <Sheet open={!!target} onClose={onClose} labelledBy="products-in-title" testId="products-in-sheet" className="sm:max-w-[640px]">
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex items-start justify-between gap-3 border-b p-4">
          <h2 id="products-in-title" className="min-w-0 break-words text-base font-semibold">
            {t('productsInTitle', { name: target?.name ?? '' })}
          </h2>
          <Button variant="ghost" size="sm" data-sheet-initial onClick={onClose} aria-label={t('closeLabel')} className="min-h-[44px] min-w-[44px] shrink-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <SkeletonTable cols={3} />
          ) : isError ? (
            <p className="text-sm text-destructive-text">{t('productsInFailed')}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('productsInNone')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-start text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-start font-medium">{t('productName')}</th>
                  <th className="py-2 text-start font-medium">SKU</th>
                  <th className="py-2 text-end font-medium">{t('productsInPrice')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2 pe-2 font-medium break-words">{p.name}</td>
                    <td className="py-2 pe-2 text-muted-foreground">{p.sku || '—'}</td>
                    <td className="py-2 text-end tabular-nums">{p.salePrice != null ? formatCurrency(Number(p.salePrice)) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t p-4 text-sm">
          <span className="text-muted-foreground">{total > LIMIT ? t('productsInShowing', { shown: LIMIT, total }) : t('productsInCount', { count: total })}</span>
          <Link to="/catalog/products" className="font-medium text-primary-text underline">{t('openInProducts')}</Link>
        </div>
      </div>
    </Sheet>
  );
}
